// Settings — the vocabularies, the branding, and the data escape hatches.
//
// Everything here is stored on the server beside the log rather than in env or in
// localStorage. That's the point: the words your team uses travel with the data,
// reach everyone, and are still readable by an agent that never runs this app —
// and a deployment's own name and colour are data too, so changing them is not a
// redeploy.
//
// Keys vs labels: a stage or state KEY is referenced by every event that ever used
// it, and some keys are load-bearing (`won`/`lost` decide what counts as money;
// `inactive` decides what leaves the rollups). So labels and colors are editable
// everywhere, and keys are only editable on the things you add yourself.

import { useState } from 'react'

import { ConfirmAction, EditableText, Section } from '../components/common'
import { Icon } from '../components/Icon'
import { Page } from '../components/Page'
import { colorVar, ORG_STATES, PALETTE, type Config, type StageKind } from '../lib/model'
import { entitiesOfKind, fieldStr, stageOf } from '../lib/reducer'
import { useStore } from '../lib/store'
import { AgentSeatsPanel } from './AgentSeatsPanel'
import { BrandingPanel } from './BrandingPanel'
import { ExportPanel, ImportPanel, LocalCachePanel, SampleDataPanel } from './ImportPage'
import { TeamPanel } from './TeamPanel'

const STAGE_KIND_COLOR: Record<string, string> = {
  open: 'var(--ember)',
  won: 'var(--good)',
  delivery: 'var(--good)',
  closed: 'var(--marine)',
  lost: 'var(--crit)',
}

const STAGE_KIND_HELP: Record<string, string> = {
  open: 'In the pipeline, might not happen',
  won: 'Signed, work not started',
  delivery: 'Signed and running, counts as live work',
  closed: 'Delivered and wrapped up',
  lost: "Didn't happen",
}

/** A swatch row for picking one of the named palette colors. */
type GroupKey = 'deployment' | 'vocabulary' | 'agents' | 'data'

const GROUPS: Array<{ key: GroupKey; label: string }> = [
  { key: 'deployment', label: 'Deployment' },
  { key: 'vocabulary', label: 'Vocabulary' },
  { key: 'agents', label: 'Agent seats' },
  { key: 'data', label: 'Data' },
]

function ColorPicker({ value, onPick }: { value: string; onPick: (c: string) => void }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {Object.keys(PALETTE).map((c) => (
        <button
          key={c}
          type="button"
          title={c}
          onClick={() => onPick(c)}
          className="h-4 w-4 rounded-full"
          style={{
            background: `var(${colorVar(c)})`,
            outline: value === c ? `2px solid var(${colorVar(c)})` : 'none',
            outlineOffset: 2,
            opacity: value === c ? 1 : 0.55,
          }}
        />
      ))}
    </span>
  )
}

export function SettingsPage() {
  const { state, config, saveConfig, version } = useStore()
  void version
  const [saved, setSaved] = useState(false)
  const [group, setGroup] = useState<GroupKey>('deployment')

  const write = (next: Partial<Config>) => {
    void saveConfig({ ...config, ...next })
    setSaved(true)
    window.setTimeout(() => setSaved(false), 1400)
  }

  // How many records use each label, so you can see what a rename touches — and
  // what you'd be stranding if you removed it.
  const deals = entitiesOfKind(state, 'deal')
  const projects = entitiesOfKind(state, 'project')
  const stageUse = (key: string) => deals.filter((d) => stageOf(d, config.stages) === key).length
  const artifacts = entitiesOfKind(state, 'artifact')

  return (
    <Page
      title="Settings"
      right={
        saved ? (
          <span className="pill" style={{ '--pc': 'var(--good)' } as React.CSSProperties}>
            <span className="tick" /> saved
          </span>
        ) : undefined
      }
    >
      {/* One page had grown to eight stacked sections, so finding the one you came
          for meant scrolling past seven you did not. A list on the left, one group
          at a time — the page is long because a deployment has a lot of settings,
          not because any one group is. */}
      <div className="flex flex-col gap-8 lg:flex-row lg:gap-10">
        <nav className="shrink-0 lg:w-[11rem]">
          <ul className="flex flex-wrap gap-1 lg:block lg:space-y-0.5">
            {GROUPS.map((g) => (
              <li key={g.key}>
                <button
                  type="button"
                  onClick={() => setGroup(g.key)}
                  className="rounded-control text-body w-full px-2.5 py-1.5 text-left"
                  style={{
                    background: group === g.key ? 'var(--rail-hover)' : 'transparent',
                    color: group === g.key ? 'var(--fg)' : 'var(--muted)',
                    fontWeight: group === g.key ? 560 : 450,
                  }}
                >
                  {g.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 flex-1">
          {group === 'deployment' && (
            <>
              <BrandingPanel />
              <TeamPanel />
            </>
          )}
          {group === 'agents' && <AgentSeatsPanel />}

          {group === 'vocabulary' && (
            <>
              {/* ── deal stages ─────────────────────────────────────────────────────── */}
              <Section
                title="Deal stages"
                note="idea → won, in your words"
                right={
                  <button
                    type="button"
                    className="adder"
                    onClick={() =>
                      write({
                        stages: [
                          ...config.stages,
                          {
                            key: `stage_${Date.now().toString(36)}`,
                            label: 'New stage',
                            kind: 'open',
                          },
                        ],
                      })
                    }
                  >
                    <Icon name="Plus" size={12} /> Add stage
                  </button>
                }
              >
                <div className="rows">
                  {config.stages.map((s, i) => (
                    <div key={s.key} className="py-2.5">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <span className="flex w-5 flex-col" style={{ color: 'var(--faint)' }}>
                          <button
                            type="button"
                            disabled={i === 0}
                            title="Move up"
                            onClick={() => {
                              const next = [...config.stages]
                              const [row] = next.splice(i, 1)
                              next.splice(i - 1, 0, row!)
                              write({ stages: next })
                            }}
                            style={{ opacity: i === 0 ? 0.25 : 1, lineHeight: 0.8 }}
                          >
                            <Icon
                              name="ChevronRight"
                              size={12}
                              style={{ transform: 'rotate(-90deg)' }}
                            />
                          </button>
                          <button
                            type="button"
                            disabled={i === config.stages.length - 1}
                            title="Move down"
                            onClick={() => {
                              const next = [...config.stages]
                              const [row] = next.splice(i, 1)
                              next.splice(i + 1, 0, row!)
                              write({ stages: next })
                            }}
                            style={{
                              opacity: i === config.stages.length - 1 ? 0.25 : 1,
                              lineHeight: 0.8,
                            }}
                          >
                            <Icon name="ChevronDown" size={12} />
                          </button>
                        </span>

                        <span className="min-w-[9rem] flex-1">
                          <EditableText
                            value={s.label}
                            className="text-body font-medium"
                            style={{ marginLeft: -8 }}
                            onSave={(label) =>
                              write({
                                stages: config.stages.map((x) =>
                                  x.key === s.key ? { ...x, label } : x,
                                ),
                              })
                            }
                          />
                          <span className="meta ml-1">{s.key}</span>
                        </span>

                        {/* The SEMANTIC, which is what every rollup reads — the label above is
                          yours, this is not. `delivery` is what lets a signed deal still be
                          live work, and `closed` is what lets it finish. */}
                        <span className="flex flex-wrap items-center gap-1">
                          {(['open', 'won', 'delivery', 'closed', 'lost'] as StageKind[]).map(
                            (k) => (
                              <button
                                key={k}
                                type="button"
                                title={STAGE_KIND_HELP[k]}
                                onClick={() =>
                                  write({
                                    stages: config.stages.map((x) =>
                                      x.key === s.key ? { ...x, kind: k } : x,
                                    ),
                                  })
                                }
                                className="rounded-inline px-2 py-0.5"
                                style={{
                                  fontFamily: 'var(--mono)',
                                  fontSize: 'var(--text-micro)',
                                  letterSpacing: '0.05em',
                                  textTransform: 'uppercase',
                                  color: s.kind === k ? 'var(--panel)' : 'var(--muted)',
                                  background: s.kind === k ? STAGE_KIND_COLOR[k] : 'transparent',
                                  border: `1px solid ${s.kind === k ? 'transparent' : 'var(--border)'}`,
                                }}
                              >
                                {k}
                              </button>
                            ),
                          )}
                        </span>

                        <span
                          className="num w-16 text-right"
                          style={{ fontSize: 'var(--text-label)', color: 'var(--faint)' }}
                        >
                          {stageUse(s.key)} {stageUse(s.key) === 1 ? 'deal' : 'deals'}
                        </span>
                        <ConfirmAction
                          icon="Trash2"
                          title={
                            stageUse(s.key) > 0
                              ? `${stageUse(s.key)} deals sit here; they'll fall back to the first open stage`
                              : 'Remove this stage'
                          }
                          confirmLabel="Remove"
                          onConfirm={() =>
                            write({ stages: config.stages.filter((x) => x.key !== s.key) })
                          }
                        />
                      </div>
                      {/* What the stage MEANS. Shown on the Deals page under the heading and in
                         the stage menu, so nobody has to guess what "Qualified" covers. */}
                      <div className="mt-1 pl-8">
                        <EditableText
                          value={s.description ?? ''}
                          placeholder="What does this stage mean? (shown on the Deals page)"
                          className="text-meta"
                          style={{ marginLeft: -8, color: 'var(--muted)' }}
                          onSave={(description) =>
                            write({
                              stages: config.stages.map((x) =>
                                x.key === s.key ? { ...x, description } : x,
                              ),
                            })
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="note mt-2">
                  Removing a stage never edits history: a deal whose stage no longer exists falls
                  back to the first open stage, and its old stage still reads in the log. The
                  semantics below each label are what the rollups count. <b>Signed</b> is won plus
                  in-delivery.
                </p>
              </Section>

              <KindsSection
                field="projectKinds"
                title="Project kinds"
                note="the filters on the Projects list"
                countFor={(key) => projects.filter((p) => fieldStr(p, 'kind') === key).length}
                write={write}
              />

              <KindsSection
                field="artifactKinds"
                title="Artifact kinds"
                note="the filters on the Artifacts list"
                countFor={(key) => artifacts.filter((a) => fieldStr(a, 'kind') === key).length}
                write={write}
              />
            </>
          )}

          {group === 'data' && (
            <>
              <ImportPanel />
              <ExportPanel />
              <LocalCachePanel />
              <SampleDataPanel />

              <Section title="Where this lives" note="on this deployment, and nowhere else">
                <div className="rows">
                  {[
                    ['events', 'the append-only log · the source of truth · one row per event'],
                    ['config', 'everything on this page, including the branding'],
                    [
                      'GET /api/export',
                      'all of it as JSONL, any time — the backup and the way out',
                    ],
                  ].map(([path, what]) => (
                    <div key={path} className="flex flex-wrap items-baseline gap-x-3 py-2">
                      <span className="num" style={{ fontSize: 'var(--text-meta)' }}>
                        {path}
                      </span>
                      <span className="meta">{what}</span>
                    </div>
                  ))}
                </div>
              </Section>
            </>
          )}
        </div>
      </div>
    </Page>
  )
}

/** Org states are the one vocabulary with nothing to configure — two values, both
 *  structural. Kept out of the page rather than shown as a dead row. */
void ORG_STATES

/**
 * One editable vocabulary: the labels a kind's records are filtered and pilled by.
 *
 * Generic because there are two of them and they were one — the section only ever
 * edited `projectKinds`, so `artifactKinds` shipped with defaults nobody could
 * change and a Settings page that silently claimed to cover both.
 */
function KindsSection({
  field,
  title,
  note,
  countFor,
  write,
}: {
  field: 'projectKinds' | 'artifactKinds'
  title: string
  note: string
  countFor: (key: string) => number
  write: (next: Partial<Config>) => void
}) {
  const { config } = useStore()
  const list = config[field]

  return (
    <Section
      title={title}
      note={note}
      right={
        <button
          type="button"
          className="adder"
          onClick={() =>
            write({
              [field]: [
                ...list,
                {
                  key: `kind_${Date.now().toString(36)}`,
                  label: 'New kind',
                  plural: 'New kinds',
                  color: 'grey',
                },
              ],
            })
          }
        >
          <Icon name="Plus" size={14} /> Add
        </button>
      }
    >
      <div className="rows">
        {list.map((k) => (
          <div key={k.key} className="flex flex-wrap items-center gap-2 py-2">
            <span className="min-w-[8rem] flex-1">
              <EditableText
                value={k.label}
                placeholder="Label"
                onSave={(label) =>
                  write({ [field]: list.map((x) => (x.key === k.key ? { ...x, label } : x)) })
                }
              />
            </span>
            <span className="min-w-[8rem] flex-1">
              <EditableText
                value={k.plural ?? ''}
                placeholder="Plural"
                onSave={(plural) =>
                  write({ [field]: list.map((x) => (x.key === k.key ? { ...x, plural } : x)) })
                }
              />
            </span>
            <ColorPicker
              value={k.color ?? 'grey'}
              onPick={(color) =>
                write({ [field]: list.map((x) => (x.key === k.key ? { ...x, color } : x)) })
              }
            />
            <span
              className="num w-14 text-right"
              style={{ fontSize: 'var(--text-label)', color: 'var(--faint)' }}
            >
              {countFor(k.key)}
            </span>
            <ConfirmAction
              icon="Trash2"
              title="Remove this kind"
              confirmLabel="Remove"
              onConfirm={() => write({ [field]: list.filter((x) => x.key !== k.key) })}
            />
          </div>
        ))}
      </div>
    </Section>
  )
}
